import React from 'react';
import { Compass, Square, X, Maximize2, Monitor, MonitorOff } from 'lucide-react';

export default function CoBrowseTopBar({
  isPreparingCoBrowse = false,
  isCoBrowsing = false,
  studentName = 'Student',
  canDesktopScreenShare = false,
  screenSharing = false,
  onToggleScreenShare,
  onStartCoBrowsing,
  onStopCoBrowsing,
  onCancelPrep,
  onExpandClass,
}) {
  if (!isPreparingCoBrowse && !isCoBrowsing) {
    return null;
  }

  return (
    <aside
      aria-label="Co-Browsing Controls"
      className="fixed top-3 sm:top-5 left-1/2 -translate-x-1/2 z-[99999] pointer-events-auto select-none max-w-[calc(100vw-24px)] animate-in fade-in slide-in-from-top-3 duration-300"
    >
      <style>{`
        @keyframes cobrowse-shimmer {
          0% { transform: translateX(-150%) skewX(-20deg); }
          40%, 100% { transform: translateX(250%) skewX(-20deg); }
        }
        @keyframes cobrowse-glow {
          0%, 100% {
            box-shadow: 0 0 16px rgba(16, 185, 129, 0.45), 0 0 32px rgba(16, 185, 129, 0.25);
          }
          50% {
            box-shadow: 0 0 28px rgba(16, 185, 129, 0.8), 0 0 55px rgba(16, 185, 129, 0.45);
          }
        }
        @keyframes cobrowse-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* State 1: Preparation Phase (Waiting for teacher to pick content and click Start) */}
      {isPreparingCoBrowse && !isCoBrowsing && (
        <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-slate-950/95 border border-emerald-500/50 shadow-[0_10px_35px_-5px_rgba(16,185,129,0.35),0_0_15px_rgba(0,0,0,0.7)] backdrop-blur-xl text-white">
          {/* Pulsing Prep Radar Status */}
          <div className="flex items-center gap-2 pl-1.5 sm:pl-2">
            <div className="relative flex items-center justify-center">
              <span className="absolute -inset-1 rounded-full bg-emerald-400/60 animate-ping" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 hidden sm:inline">
              Prep Mode
            </span>
          </div>

          <div className="h-5 w-px bg-slate-700/80 hidden sm:block" />

          {/* Helper caption for desktop */}
          <span className="text-xs text-slate-300 hidden md:inline font-medium">
            Navigate to lesson or page, then:
          </span>

          {/* Prominent Glowing "Start Co-Browsing" Action Button */}
          <button
            type="button"
            onClick={onStartCoBrowsing}
            style={{ animation: 'cobrowse-glow 2.5s infinite ease-in-out' }}
            className="relative group overflow-hidden px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-bold text-xs sm:text-sm text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-400 hover:to-teal-400 transition-all duration-200 transform hover:scale-[1.03] active:scale-[0.98] flex items-center gap-2 ring-2 ring-emerald-300/60 ring-offset-2 ring-offset-slate-950 cursor-pointer"
            title="Start streaming this view to your student"
          >
            {/* Shimmer sweep effect */}
            <span
              className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"
              style={{ animation: 'cobrowse-shimmer 2.5s infinite ease-in-out' }}
            />

            <Compass
              className="w-4 h-4 sm:w-4.5 sm:h-4.5 shrink-0 text-white"
              style={{ animation: 'cobrowse-spin-slow 8s linear infinite' }}
            />
            <span className="tracking-wide font-extrabold whitespace-nowrap">Start Co-Browsing</span>
            <span className="hidden xs:inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/20 text-[10px] font-black">
              →
            </span>
          </button>

          {/* Quick Screen Share option from Prep Mode */}
          {canDesktopScreenShare && onToggleScreenShare && (
            <button
              type="button"
              onClick={onToggleScreenShare}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-full font-bold text-xs sm:text-sm text-white transition-all flex items-center gap-1.5 cursor-pointer ${
                screenSharing
                  ? 'bg-amber-600 hover:bg-amber-500 border border-amber-400/50 shadow-md shadow-amber-600/30'
                  : 'bg-slate-800/90 hover:bg-slate-800 border border-slate-700 text-slate-200'
              }`}
              title={screenSharing ? 'Stop Desktop Screen Sharing' : 'Switch directly to Screen Share instead'}
            >
              {screenSharing ? <MonitorOff className="w-3.5 h-3.5 shrink-0" /> : <Monitor className="w-3.5 h-3.5 shrink-0" />}
              <span className="whitespace-nowrap hidden sm:inline">{screenSharing ? 'Stop Screen' : 'Share Screen'}</span>
            </button>
          )}

          {/* Cancel Prep Option */}
          {onCancelPrep && (
            <button
              type="button"
              onClick={onCancelPrep}
              className="p-1.5 sm:p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800/80 transition"
              title="Cancel Prep & Return to Classroom"
              aria-label="Cancel preparation"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* State 2: Active Co-Browsing Phase (Syncing view to student) */}
      {isCoBrowsing && (
        <div className="flex items-center gap-2.5 sm:gap-3.5 px-3 sm:px-4 py-2 rounded-full bg-slate-950/95 border border-emerald-500/50 shadow-[0_10px_35px_-5px_rgba(16,185,129,0.35),0_0_15px_rgba(0,0,0,0.7)] backdrop-blur-xl text-white">
          {/* Active Broadcast Indicator */}
          <div className="flex items-center gap-2 pl-0.5">
            <div className="relative flex items-center justify-center">
              <span className={`absolute -inset-1 rounded-full ${screenSharing ? 'bg-amber-400/60' : 'bg-emerald-400/60'} animate-ping`} />
              <span className={`relative h-2.5 w-2.5 rounded-full ${screenSharing ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]' : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]'}`} />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs sm:text-sm font-bold text-white whitespace-nowrap">
                {screenSharing ? 'Screen Share Active' : 'Co-Browsing Active'}
              </span>
              {studentName && (
                <span className={`text-[11px] ${screenSharing ? 'text-amber-300' : 'text-emerald-300'} font-medium truncate max-w-[120px] sm:max-w-[180px] hidden sm:inline`}>
                  {screenSharing ? `• Sharing with ${studentName}` : `• Syncing with ${studentName}`}
                </span>
              )}
            </div>
          </div>

          <div className="h-5 w-px bg-slate-700/80" />

          {/* Quick-switch to WebRTC Screen Share directly from Co-Browsing */}
          {canDesktopScreenShare && onToggleScreenShare && (
            <button
              type="button"
              onClick={onToggleScreenShare}
              className={`px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full font-bold text-xs sm:text-sm text-white transition-all duration-150 transform hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1.5 border shrink-0 cursor-pointer ${
                screenSharing
                  ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 border-amber-400/50 shadow-md shadow-amber-600/30'
                  : 'bg-slate-800/90 hover:bg-slate-800 border-slate-700 text-slate-200'
              }`}
              title={screenSharing ? 'Stop screen sharing and continue co-browsing' : 'Share your screen directly with student without leaving co-browsing'}
            >
              {screenSharing ? <MonitorOff className="w-3.5 h-3.5 shrink-0" /> : <Monitor className="w-3.5 h-3.5 shrink-0" />}
              <span className="whitespace-nowrap">{screenSharing ? 'Stop Screen' : 'Share Screen'}</span>
            </button>
          )}

          {/* Stop Co-Browsing Action Button */}
          <button
            type="button"
            onClick={onStopCoBrowsing}
            className="px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold text-xs sm:text-sm text-white bg-gradient-to-r from-rose-600 via-rose-500 to-red-600 hover:from-rose-500 hover:to-red-500 shadow-md shadow-rose-600/30 hover:shadow-rose-600/50 transition-all duration-150 transform hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1.5 border border-rose-400/40 shrink-0 cursor-pointer"
            title="Stop co-browsing and return student to classroom"
          >
            <Square className="w-3 h-3 fill-white shrink-0" />
            <span className="whitespace-nowrap">Stop Co-Browsing</span>
          </button>

          {/* Quick Expand to Full Classroom */}
          {onExpandClass && (
            <button
              type="button"
              onClick={onExpandClass}
              className="p-1.5 sm:p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800/80 transition shrink-0"
              title="Expand to Full Classroom View"
              aria-label="Expand classroom"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
